import { Children, ComponentProps, ReactNode } from 'react'
import { Badge as Ds2Badge, Button as Ds2Button, Spinner as Ds2Spinner } from '@/components/ds2'

// The billing screens were written against the legacy `components/ds` Button / Badge / Spinner props.
// These thin wrappers keep those call sites unchanged and render the ds2 equivalents, so the move to
// Design System v2 is a styling change only — handlers, disabled and loading logic stay as written.

type Ds2ButtonProps = ComponentProps<typeof Ds2Button>
type LegacyVariant = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'danger-soft' | 'ghost' | 'outline'

const BUTTON_VARIANT: Record<LegacyVariant, Ds2ButtonProps['variant']> = {
    primary: 'primary',
    // Legacy `secondary` was the bordered neutral button; ds2's closest match is `outline`.
    secondary: 'outline',
    tertiary: 'tertiary',
    danger: 'danger',
    'danger-soft': 'dangerSoft',
    ghost: 'ghost',
    outline: 'outline',
}

interface BillingButtonProps extends Omit<Ds2ButtonProps, 'variant' | 'prefix' | 'suffix' | 'loading'> {
    variant?: LegacyVariant
    loading?: boolean
    icon?: ReactNode
    iconRight?: ReactNode
    fullWidth?: boolean
}

export const Button = ({ variant = 'primary', loading, icon, iconRight, fullWidth, className, disabled, children, ...rest }: BillingButtonProps) => (
    <Ds2Button
        {...rest}
        variant={BUTTON_VARIANT[variant]}
        // ds2 `loading` hides the label; keep it visible and show the spinner as the prefix instead.
        prefix={loading ? <Ds2Spinner size={14} color="current" /> : icon}
        suffix={iconRight}
        disabled={disabled || loading}
        className={[fullWidth ? 'w-full' : '', className ?? ''].filter(Boolean).join(' ') || undefined}
    >
        {children}
    </Ds2Button>
)

interface BillingBadgeProps {
    variant?: 'primary' | 'secondary' | 'soft'
    type?: 'accent' | 'default' | 'success' | 'warning' | 'danger'
    size?: 'sm' | 'md' | 'lg'
    icon?: ReactNode
    className?: string
    children?: ReactNode
}

// ds2 Badge takes a plain `label`; legacy call sites pass text children such as `Save {pct}%`, which arrive as an array.
const childrenToText = (children: ReactNode): string => Children.toArray(children).map(String).join('')

export const Badge = ({ variant = 'primary', type = 'default', size = 'md', icon, className, children }: BillingBadgeProps) => (
    <Ds2Badge variant={variant} type={type} size={size} prefix={icon} className={className} label={childrenToText(children)} />
)

export const Spinner = ({ size = 'md', className }: { size?: 'sm' | 'md' | 'lg'; className?: string }) => (
    <span className={className}>
        <Ds2Spinner size={size === 'sm' ? 16 : size === 'lg' ? 32 : 24} color="current" />
    </span>
)
